import { CommonModule } from '@angular/common';
import { Component, forwardRef, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Color preset interface with both tag color and text color
 */
export interface ColorPreset {
    tagColor: string;
    tagTextColor: string;
    label: string;
    value: string;
}

export interface ColorPresetConfig {
  // Required color presets array
  colorPresets: ColorPreset[]; // Array of color presets to display
  // Dependency tracking for automatic reloading
  dependsOn?: string; // Field key that this field depends on for dependency tracking
  onDependencyChange?: (dependencyValue: any) => void; // Callback when dependency changes
  placeholder?: string;
  disabled?: boolean;
}

@Component({
  selector: 'generic-colorpreset',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GenericColorPresetComponent),
      multi: true
    }
  ],
  template: `
    <div class="flex">
      <div class="color-preset-grid grid grid-cols-8 gap-2 p-2">
        <div 
          *ngFor="let preset of presets; trackBy: trackByValue"
          class="color-preset-item w-8 h-8 rounded cursor-pointer border-2 transition-all duration-200 hover:scale-110"
          [class.selected]="value === preset.value"
          [style.background-color]="preset.tagColor"
          [style.border-color]="value === preset.value ? '#000' : 'transparent'"
          [title]="preset.label"
          (click)="selectPreset(preset)"
          [class.disabled]="disabled">
        </div>
      </div>
    </div>
  `,
  styles: [`
    .color-preset-item.disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    .color-preset-item.disabled:hover {
      transform: none;
    }
    
    .color-preset-item.selected {
      box-shadow: 0 0 0 2px #000;
    }
  `]
})
export class GenericColorPresetComponent implements OnInit, OnChanges, ControlValueAccessor {
  @Input() id: string = '';
  @Input() config!: ColorPresetConfig; // Required config with colorPresets
  @Input() disabled: boolean = false;
  @Input() dependencyValue: any = null; // Value that this component depends on

  presets: ColorPreset[] = [];
  selectedPreset: ColorPreset | null = null;

  // ControlValueAccessor properties
  value: any = null;
  onChange = (value: any) => { };
  onTouched = () => { };

  ngOnInit() {
    if (!this.config) {
      throw new Error('GenericColorPresetComponent requires a config input with colorPresets');
    }

    // Use the required colorPresets from config
    this.presets = this.config.colorPresets;
  }

  ngOnChanges(changes: SimpleChanges) {
    // Handle dependency changes
    if (changes['dependencyValue'] && !changes['dependencyValue'].firstChange) {
      const currentValue = changes['dependencyValue'].currentValue;
      const previousValue = changes['dependencyValue'].previousValue;

      // Only process if the value actually changed
      if (currentValue !== previousValue) {
        // Call onDependencyChange callback if available
        if (this.config.onDependencyChange) {
          this.config.onDependencyChange(currentValue);
        }
      }
    }

    // Handle config changes
    if (changes['config'] && !changes['config'].firstChange) {
      this.presets = this.config.colorPresets;
      this.updateSelectedPreset();
    }
  }

  selectPreset(preset: ColorPreset) {
    if (this.disabled) return;
    
    this.value = preset.value;
    this.selectedPreset = preset;
    this.onChange(preset.value);
    this.onTouched();
  }

  private updateSelectedPreset() {
    this.selectedPreset = this.presets.find(preset => preset.value === this.value) || null;
  }

  trackByValue(_index: number, preset: ColorPreset): string {
    return preset.value;
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    this.value = value;
    this.updateSelectedPreset();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Public methods
  getSelectedPreset(): ColorPreset | null {
    return this.selectedPreset;
  }

  clearSelection() {
    this.value = null;
    this.selectedPreset = null;
    this.onChange(null);
  }
}
